import { Link } from "react-router-dom";
import {
  IGameRecord,
  IBanList,
  IMatch,
  IMatchGamePlayer,
  IPlayer,
  IPlayerGameStats,
  ITeamInfo,
} from "../../types/types";
import { useLocalization } from "../../hooks/useLocalization";
import { useLocalePath } from "../../hooks/useLocalePath";
import { TeamLogo } from "../shared/TeamLogo";
import {
  championIconForBan,
  championIconForPlayer,
  dragonIconUrl,
  dragonLabel,
  genericDragonIconUrl,
  isNoBanChampionId,
  itemIconUrl,
  objectiveIconUrl,
} from "../../utils/lolAssets";
import { getMultikillLabel } from "../../utils/multikill";
import {
  TeamSideMapping,
  computeCs15Diff,
  computeKpPct,
  hasCs15Data,
  formatCompactNumber,
  formatGameDuration,
  formatPct,
  formatSignedNumber,
  indexPlayerStats,
  normalizeRole,
  resolvePlayerName,
  sortPlayersByRole,
  teamKillTotal,
  teamObjectives,
  teamWonGame,
} from "../../utils/matchGameSides";
import { getPlayerProfilePath } from "../../utils/playerProfilePath";

interface MatchGameViewProps {
  game: IGameRecord;
  gameIndex: number;
  match: IMatch;
  teamInfos: ITeamInfo[];
  playerStats: IPlayerGameStats[];
  mapping: TeamSideMapping;
  rosterByPuuid: Map<string, IPlayer>;
}

const teamName = (teamInfos: ITeamInfo[], teamId: string, fallback?: string) =>
  teamInfos.find((team) => team.id === teamId)?.name ?? fallback ?? teamId;

const formatKda = (kills: number, deaths: number, assists: number) =>
  `${String(kills)}/${String(deaths)}/${String(assists)}`;

const multikillFromPlayer = (player: IMatchGamePlayer) =>
  getMultikillLabel({
    pentaKills: player.penta_kills,
    quadraKills: player.quadra_kills,
    tripleKills: player.triple_kills,
    doubleKills: player.double_kills,
  });

const hasElementalDragonData = (dragonTypes: string[]): boolean => dragonTypes.length > 0;

const MK_BADGE_CLASSNAMES: Record<string, string> = {
  penta: "bg-[rgba(255,193,7,0.2)] text-[#ffc107]",
  quadra: "bg-[rgba(156,39,176,0.2)] text-[#ce93d8]",
  triple: "bg-[rgba(33,150,243,0.2)] text-[#64b5f6]",
  double: "bg-[rgba(76,175,80,0.2)] text-[#81c784]",
};

const mkBadgeClassName = (multikill: string) =>
  `self-start rounded-[0.2rem] px-[0.35rem] py-[0.05rem] text-[0.6rem] font-semibold uppercase ${MK_BADGE_CLASSNAMES[multikill.toLowerCase()] ?? ""}`;

interface BanChampionProps {
  ban: IBanList;
  order: number;
  bannedLabel: string;
  bansLabel: string;
  iconClassName: string;
}

const BanChampion = ({ ban, order, bannedLabel, bansLabel, iconClassName }: BanChampionProps) => {
  const noBan = isNoBanChampionId(ban.championId);
  const alt = noBan ? bannedLabel : bannedLabel.replace("{id}", String(ban.championId));
  const title = `${bansLabel} #${String(order)}${noBan ? ` — ${bannedLabel}` : ""}`;

  return (
    <li className="list-none">
      <div className="relative h-7 w-7">
        <img
          src={championIconForBan(ban.championId)}
          alt={alt}
          title={title}
          className={iconClassName}
          loading="lazy"
        />
        <span
          className="absolute -right-[0.2rem] -bottom-[0.2rem] flex h-[0.95rem] min-w-[0.95rem] items-center justify-center rounded-full border border-white/35 bg-page px-[0.15rem] font-jetbrains text-[0.6rem] leading-none font-semibold"
          aria-hidden="true"
        >
          {order}
        </span>
      </div>
    </li>
  );
};

interface ObjectiveStatProps {
  icon: string;
  label: string;
  value: string | number;
  muted?: boolean;
}

const ObjectiveStat = ({ icon, label, value, muted = false }: ObjectiveStatProps) => (
  <div
    className={`flex flex-col items-center gap-[0.2rem] text-center${muted ? " opacity-55" : ""}`}
  >
    <img src={icon} alt="" className="h-7 w-7 object-contain" aria-hidden="true" />
    <span className="font-shentox text-[0.65rem] tracking-[0.06em] uppercase opacity-65">
      {label}
    </span>
    <span className="font-jetbrains text-[0.85rem]">{value}</span>
  </div>
);

interface TeamPanelProps {
  leagueTeamId: string;
  teamLabel: string;
  game: IGameRecord;
  mapping: TeamSideMapping;
  rosterByPuuid: Map<string, IPlayer>;
  statsByPuuid: Map<string, IPlayerGameStats>;
}

const TeamPanel = ({
  leagueTeamId,
  teamLabel,
  game,
  mapping,
  rosterByPuuid,
  statsByPuuid,
}: TeamPanelProps) => {
  const { strings } = useLocalization();
  const { localized } = useLocalePath();
  const copy = strings.MATCH_DETAIL;
  const won = teamWonGame(leagueTeamId, mapping, game);
  const riotSide = leagueTeamId === mapping.blueTeamId ? 100 : 200;
  const players = sortPlayersByRole(
    (game.players ?? []).filter((player) => player.team_id === riotSide),
  );
  const showCs15 = hasCs15Data(game.players ?? []);
  const objectives = teamObjectives(leagueTeamId, mapping, game);
  const showElementalDragons = hasElementalDragonData(objectives.dragonTypes);

  return (
    <section
      className={`overflow-hidden rounded-[var(--surface-card-radius)] border bg-[var(--surface-card-bg)] ${won ? "border-[rgba(28,152,217,0.45)] shadow-[inset_3px_0_0_var(--color-light-blue)]" : "border-[var(--surface-card-border)] opacity-[0.92]"}`}
      aria-label={`${teamLabel} ${won ? copy.VICTORY : copy.DEFEAT}`}
    >
      <header className="flex items-center justify-between gap-4 border-b border-b-[var(--surface-card-border)] bg-white/[0.03] px-4 py-[0.85rem] max-md:flex-col max-md:items-start">
        <div className="flex items-center gap-3">
          <TeamLogo teamId={leagueTeamId} alt={teamLabel} className="h-9 w-9 object-contain" />
          <div>
            <h3 className="m-0 font-shentox text-[1rem] font-semibold">{teamLabel}</h3>
            <p
              className={`m-0 mt-[0.15rem] font-jetbrains text-[0.75rem] tracking-[0.06em] uppercase opacity-65${won ? " text-light-blue opacity-100" : ""}`}
            >
              {won ? copy.VICTORY : copy.DEFEAT}
            </p>
          </div>
        </div>
        <p className="m-0 font-jetbrains text-[0.85rem]">
          {copy.KILLS}: <strong>{teamKillTotal(players)}</strong>
        </p>
      </header>

      <div
        className="grid grid-cols-[repeat(auto-fit,minmax(5.5rem,1fr))] gap-[0.65rem] border-b border-b-[var(--surface-card-border)] px-4 py-[0.85rem]"
        aria-label={copy.OBJECTIVES}
      >
        <ObjectiveStat
          icon={objectiveIconUrl("turret")}
          label={copy.TURRETS}
          value={objectives.turrets}
        />
        {showElementalDragons ? (
          <div className="col-span-2 flex flex-col items-center gap-[0.2rem] text-center">
            <span className="font-shentox text-[0.65rem] tracking-[0.06em] uppercase opacity-65">
              {copy.DRAGONS}
            </span>
            <span className="font-jetbrains text-[0.85rem]">{objectives.dragons}</span>
            <ul
              className="m-0 mt-[0.35rem] flex flex-wrap justify-center gap-[0.35rem] p-0"
              aria-label={copy.DRAGON_TYPES}
            >
              {objectives.dragonTypes.map((dragonType, index) => {
                const label = dragonLabel(dragonType);
                return (
                  <li key={`${leagueTeamId}-dragon-${String(index)}`} className="list-none">
                    <img
                      src={dragonIconUrl(dragonType)}
                      alt={label}
                      title={label}
                      className="h-[1.6rem] w-[1.6rem] object-contain"
                      loading="lazy"
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <ObjectiveStat
            icon={genericDragonIconUrl()}
            label={copy.DRAGONS}
            value={objectives.dragons}
            muted={objectives.dragons === 0}
          />
        )}
        <ObjectiveStat
          icon={objectiveIconUrl("baron")}
          label={copy.BARONS}
          value={objectives.barons}
        />
        <ObjectiveStat
          icon={objectiveIconUrl("grub")}
          label={copy.GRUBS}
          value={objectives.grubs}
        />
        <ObjectiveStat
          icon={objectiveIconUrl("herald")}
          label={copy.HERALD}
          value={objectives.herald ? copy.YES : copy.NO}
          muted={!objectives.herald}
        />
        <ObjectiveStat
          icon={objectiveIconUrl("elder")}
          label={copy.ELDERS}
          value={objectives.elders}
          muted={objectives.elders === 0}
        />
      </div>

      {objectives.bans.length > 0 && (
        <div className="flex items-center gap-[0.65rem] border-b border-b-[var(--surface-card-border)] px-4 py-[0.65rem]">
          <span className="font-shentox text-[0.7rem] tracking-[0.06em] uppercase opacity-65">
            {copy.BANS}
          </span>
          <ol className="m-0 flex flex-wrap gap-[0.35rem] p-0">
            {objectives.bans.map((ban, index) => (
              <BanChampion
                key={`${leagueTeamId}-${String(ban.pickTurn)}-${String(ban.championId)}`}
                ban={ban}
                order={index + 1}
                bannedLabel={isNoBanChampionId(ban.championId) ? copy.NO_BAN : copy.BANNED_CHAMPION}
                bansLabel={copy.BANS}
                iconClassName="h-7 w-7 rounded-full object-cover opacity-85 grayscale"
              />
            ))}
          </ol>
        </div>
      )}

      <div
        className="flex w-full flex-col overflow-x-auto max-md:pb-[0.35rem] max-md:[-webkit-overflow-scrolling:touch]"
        role="table"
        aria-label={copy.PLAYER_STATS}
      >
        <div
          className={`match-scoreboard-grid${showCs15 ? "" : " match-scoreboard-grid--no-cs15"} items-center gap-2 border-b border-b-[var(--surface-card-border)] px-3 py-[0.55rem] font-shentox text-[0.65rem] tracking-[0.06em] uppercase opacity-65 max-md:gap-[0.6rem] max-md:px-[0.85rem] max-md:py-[0.6rem] max-md:text-[0.6rem]`}
          role="row"
        >
          <span role="columnheader">{copy.CHAMPION}</span>
          <span role="columnheader">{copy.PLAYER}</span>
          <span role="columnheader">KDA</span>
          <span role="columnheader">CS</span>
          {showCs15 && <span role="columnheader">{copy.CS15_DIFF}</span>}
          <span role="columnheader">{copy.DAMAGE}</span>
          <span role="columnheader">{copy.GOLD}</span>
          <span role="columnheader">{copy.VISION}</span>
          <span role="columnheader">KP</span>
          <span role="columnheader">{copy.ITEMS}</span>
        </div>

        {players.map((player) => {
          const rosterPlayer = rosterByPuuid.get(player.puuid);
          const stat = statsByPuuid.get(player.puuid);
          const playerName = resolvePlayerName(player, rosterByPuuid, statsByPuuid);
          const profilePath = localized(
            getPlayerProfilePath(rosterPlayer?.name ?? stat?.playerName ?? playerName),
          );
          const multikill = multikillFromPlayer(player);
          const csPerMin =
            stat?.csPerMin ?? (game.duration > 0 ? player.creep_score / game.duration : undefined);
          const dpm =
            stat?.dpm ??
            (game.duration > 0 ? Math.round(player.damage / game.duration) : undefined);
          const kpPct = computeKpPct(player, players);
          const cs15Diff = computeCs15Diff(player, game.players ?? []);

          return (
            <div
              key={player.puuid}
              className={`match-scoreboard-grid${showCs15 ? "" : " match-scoreboard-grid--no-cs15"} items-center gap-2 border-b border-b-white/5 px-3 py-[0.55rem] font-jetbrains text-[0.8rem] last:border-b-0 max-md:gap-[0.6rem] max-md:px-[0.85rem] max-md:py-[0.6rem] max-md:text-[0.75rem]`}
              role="row"
            >
              <div className="relative h-10 w-10" role="cell">
                <img
                  src={championIconForPlayer(player)}
                  alt={player.champion_name}
                  className="h-full w-full rounded-[0.35rem] object-cover"
                  loading="lazy"
                />
                <span className="absolute -right-[0.15rem] -bottom-[0.15rem] rounded-[0.2rem] bg-page px-[0.25rem] py-[0.05rem] text-[0.6rem] leading-[1.2]">
                  Lv{player.level}
                </span>
              </div>

              <div className="flex min-w-0 flex-col gap-[0.1rem]" role="cell">
                <Link
                  to={profilePath}
                  className="font-medium text-white no-underline hover:text-light-blue"
                >
                  {playerName}
                </Link>
                <span className="text-[0.65rem] uppercase opacity-65">
                  {normalizeRole(player.role)}
                </span>
                {multikill && <span className={mkBadgeClassName(multikill)}>{multikill}</span>}
              </div>

              <span className="flex min-w-0 flex-col gap-[0.1rem]" role="cell">
                {formatKda(player.champions_killed, player.deaths, player.assists)}
              </span>
              <span className="flex min-w-0 flex-col gap-[0.1rem]" role="cell">
                {player.creep_score}
                {csPerMin != null && (
                  <span className="block text-[0.65rem] opacity-70 max-md:whitespace-nowrap">
                    {csPerMin.toFixed(1)} {copy.CS_PER_MIN}
                  </span>
                )}
              </span>
              {showCs15 && (
                <span
                  className={`flex min-w-0 flex-col gap-[0.1rem]${cs15Diff != null && cs15Diff >= 0 ? " text-[#81c784]" : ""}${cs15Diff != null && cs15Diff < 0 ? " text-[#e57373]" : ""}`}
                  role="cell"
                >
                  {cs15Diff == null ? "—" : formatSignedNumber(cs15Diff)}
                </span>
              )}
              <span className="flex min-w-0 flex-col gap-[0.1rem]" role="cell">
                {formatCompactNumber(player.damage)}
                {dpm != null && (
                  <span className="block text-[0.65rem] opacity-70 max-md:whitespace-nowrap">
                    {dpm} {copy.DPM}
                  </span>
                )}
                {stat?.dmgPct != null && (
                  <span className="block text-[0.65rem] opacity-70 max-md:whitespace-nowrap">
                    {formatPct(stat.dmgPct)}
                  </span>
                )}
              </span>
              <span className="flex min-w-0 flex-col gap-[0.1rem]" role="cell">
                {formatCompactNumber(player.gold_earned)}
                {stat?.goldPct != null && (
                  <span className="block text-[0.65rem] opacity-70 max-md:whitespace-nowrap">
                    {formatPct(stat.goldPct)}
                  </span>
                )}
              </span>
              <span className="flex min-w-0 flex-col gap-[0.1rem]" role="cell">
                {player.vision_score}
                <span className="block text-[0.65rem] opacity-70 max-md:whitespace-nowrap">
                  {player.wards_placed} / {player.wards_killed}
                </span>
              </span>
              <span className="flex min-w-0 flex-col gap-[0.1rem]" role="cell">
                {formatPct(kpPct)}
              </span>
              <div className="flex flex-wrap gap-[0.2rem]" role="cell">
                {player.items.map((itemId, index) => {
                  const icon = itemIconUrl(itemId);
                  return icon ? (
                    <img
                      key={`${player.puuid}-item-${String(index)}`}
                      src={icon}
                      alt=""
                      className="h-6 w-6 rounded-[0.2rem] bg-white/5 object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <span
                      key={`${player.puuid}-empty-${String(index)}`}
                      className="inline-block h-6 w-6 rounded-[0.2rem] bg-white/5"
                      aria-hidden="true"
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const MatchGameView = ({
  game,
  gameIndex,
  match,
  teamInfos,
  playerStats,
  mapping,
  rosterByPuuid,
}: MatchGameViewProps) => {
  const { strings } = useLocalization();
  const copy = strings.MATCH_DETAIL;

  const gameStats = playerStats.filter((stat) => stat.riotGameId === game.game_id);
  const { byPuuid: statsByPuuid } = indexPlayerStats(gameStats);

  const teamOneLabel = teamName(teamInfos, match.teamOne, match.teamOneName);
  const teamTwoLabel = teamName(teamInfos, match.teamTwo, match.teamTwoName);
  const teamOneWon = teamWonGame(match.teamOne, mapping, game);

  const panels = [
    { id: match.teamOne, label: teamOneLabel, won: teamOneWon },
    { id: match.teamTwo, label: teamTwoLabel, won: !teamOneWon },
  ].sort((a, b) => Number(b.won) - Number(a.won));

  return (
    <article className="flex w-full flex-col" aria-labelledby={`match-game-${String(gameIndex)}`}>
      <header className="border-b border-b-[var(--surface-card-border)] bg-white/[0.02] px-4 pt-4 pb-3 text-center">
        <h2
          id={`match-game-${String(gameIndex)}`}
          className="m-0 font-shentox text-[clamp(1rem,1.6vw,1.2rem)] tracking-[0.06em] uppercase"
        >
          {copy.GAME_TITLE.replace("{number}", String(gameIndex + 1))}
        </h2>
        <p className="m-0 mt-[0.35rem] flex flex-wrap justify-center gap-[0.45rem] font-jetbrains text-[0.85rem] opacity-80">
          <span>{formatGameDuration(game.duration)}</span>
          <span aria-hidden="true">·</span>
          <span>
            {teamOneWon
              ? copy.WINNER_LABEL.replace("{team}", teamOneLabel)
              : copy.WINNER_LABEL.replace("{team}", teamTwoLabel)}
          </span>
        </p>
      </header>

      <div className="flex w-full flex-col gap-5 pb-4">
        {panels.map((panel) => (
          <TeamPanel
            key={panel.id}
            leagueTeamId={panel.id}
            teamLabel={panel.label}
            game={game}
            mapping={mapping}
            rosterByPuuid={rosterByPuuid}
            statsByPuuid={statsByPuuid}
          />
        ))}
      </div>
    </article>
  );
};

export const gameWinnerTeamId = (game: IGameRecord, mapping: TeamSideMapping): string =>
  game.blue_win ? mapping.blueTeamId : mapping.redTeamId;

export const gameWinnerLabel = (
  game: IGameRecord,
  mapping: TeamSideMapping,
  teamInfos: ITeamInfo[],
  match: IMatch,
): string => {
  const winnerId = gameWinnerTeamId(game, mapping);
  if (winnerId === match.teamOne) {
    return match.teamOneName || teamName(teamInfos, match.teamOne);
  }
  return match.teamTwoName || teamName(teamInfos, match.teamTwo);
};
